/**
 * Customer email templates, and the ROOF.link export they came from.
 *
 * Two jobs live here. The first is the merge field vocabulary: the tokens a
 * template may contain and how they are filled from a job. The second is the
 * one-off translation of the 98 templates exported from ROOF.link, which is in
 * this package rather than in the import script because it is a business rule —
 * what "{{job.estimate.tot_gross}}" means, and the fact that Roof Board has no
 * answer for "{{job.total_requested_commissions}}", is domain knowledge, and it
 * needs a test.
 *
 * Nothing here talks to a database. `convertRooflinkTemplate` takes a row of
 * the CSV and returns what should be stored; `renderEmailTemplate` takes a
 * stored body and the values for one job and returns what a person would send.
 *
 * These are not the Supabase auth emails in `supabase/email-templates/` — those
 * are the invite and password-reset messages, and have nothing to do with this.
 */

/** Where a template is used from. Mirrors `email_templates.context`. */
export type EmailContext =
  "job" | "material_order" | "work_order" | "payment" | "company";

export const EMAIL_CONTEXTS: readonly EmailContext[] = [
  "job",
  "material_order",
  "work_order",
  "payment",
  "company",
];

export const EMAIL_CONTEXT_LABELS: Readonly<Record<EmailContext, string>> = {
  job: "Job",
  material_order: "Material order",
  work_order: "Work order",
  payment: "Payment",
  company: "Company",
};

/** A row of `email_templates`. */
export interface EmailTemplate {
  id: string;
  name: string;
  context: EmailContext;
  subject: string;
  body: string;
  active: boolean;
  needs_review: boolean;
  review_notes: string[];
  source: string | null;
  source_ref: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string | null;
}

/** One merge field, as offered to a person editing a template. */
export interface EmailField {
  /** What goes between the braces. */
  token: string;
  label: string;
  group: string;
}

/**
 * Every merge field Roof Board can fill.
 *
 * Deliberately shallower than ROOF.link's — `{{customer.first_name}}` rather
 * than `{{job.customer.first_name}}` — because a template is written by a
 * person, not generated, and the extra hop never told them anything.
 */
export const EMAIL_FIELDS: readonly EmailField[] = [
  { token: "customer.name", label: "Full name", group: "Customer" },
  { token: "customer.first_name", label: "First name", group: "Customer" },
  { token: "customer.last_name", label: "Last name", group: "Customer" },
  { token: "customer.email", label: "Email", group: "Customer" },
  // The token names stay as they are — 85 stored templates carry
  // {{customer.phone}} and renaming it would empty every one of them. What the
  // picker calls it is what was actually wrong.
  { token: "customer.cell", label: "Cell", group: "Customer" },
  { token: "customer.phone", label: "Alternate phone", group: "Customer" },
  { token: "customer.address", label: "Mailing address", group: "Customer" },
  { token: "customer.city", label: "City", group: "Customer" },
  { token: "customer.state", label: "State", group: "Customer" },
  { token: "customer.zip", label: "Zip", group: "Customer" },

  { token: "job.number", label: "Job number", group: "Job" },
  { token: "job.address", label: "Service address", group: "Job" },
  { token: "job.full_address", label: "Full service address", group: "Job" },
  { token: "job.city_state_zip", label: "City, state zip", group: "Job" },
  { token: "job.type", label: "Job and bid type", group: "Job" },
  { token: "job.stage", label: "Status", group: "Job" },
  { token: "job.lead_source", label: "Lead source", group: "Job" },
  { token: "job.claim_number", label: "Claim number", group: "Job" },
  { token: "job.url", label: "Link to the job", group: "Job" },
  { token: "job.inspection_date", label: "Inspection date", group: "Job" },
  { token: "job.work_date", label: "Work date", group: "Job" },
  { token: "job.signed_date", label: "Date signed", group: "Job" },
  // No "date completed": `jobs` has no completion timestamp — only the
  // `complete` stage — so there is nothing to fill it from. It was briefly
  // offered here and resolved from `work_date`, which is the date the crew was
  // *scheduled*; on any job that slipped, "your roof was completed on the
  // 26th" would have been a wrong date in a letter to a customer.
  // `job.date_completed` from ROOF.link is mapped to null below and left
  // visible, which is the same admission the other 21 gaps get.

  { token: "rep.name", label: "Sales rep", group: "People" },
  { token: "rep.first_name", label: "Sales rep first name", group: "People" },
  { token: "rep.last_name", label: "Sales rep last name", group: "People" },
  { token: "rep.email", label: "Sales rep email", group: "People" },
  { token: "rep.phone", label: "Sales rep phone", group: "People" },
  { token: "alt_rep.name", label: "Alternate rep", group: "People" },
  { token: "pm.name", label: "Project manager", group: "People" },
  { token: "coordinator.name", label: "Coordinator", group: "People" },
  { token: "inspector.name", label: "Inspector", group: "People" },
  { token: "crew.name", label: "Crew", group: "People" },
  { token: "created_by.name", label: "Created by", group: "People" },

  { token: "estimate.total", label: "Contract total", group: "Estimate" },
  { token: "estimate.gross_profit", label: "Gross profit", group: "Estimate" },
  { token: "estimate.margin", label: "Margin", group: "Estimate" },
  { token: "estimate.materials", label: "Materials", group: "Estimate" },
  { token: "estimate.labor", label: "Labour", group: "Estimate" },
  { token: "estimate.balance", label: "Balance due", group: "Estimate" },

  {
    token: "material_order.number",
    label: "Material order number",
    group: "Orders",
  },
  { token: "work_order.number", label: "Work order number", group: "Orders" },
  { token: "work_order.start_date", label: "Start date", group: "Orders" },
  { token: "work_order.end_date", label: "End date", group: "Orders" },

  { token: "company.name", label: "Company name", group: "Company" },
  { token: "company.phone", label: "Company phone", group: "Company" },
  { token: "company.email", label: "Company email", group: "Company" },
  { token: "company.website", label: "Website", group: "Company" },
  { token: "company.facebook", label: "Facebook", group: "Company" },

  { token: "sender.name", label: "Whoever is sending", group: "Sender" },
  { token: "sender.email", label: "Sender's email", group: "Sender" },
  { token: "sender.phone", label: "Sender's phone", group: "Sender" },
];

const KNOWN_TOKENS = new Set(EMAIL_FIELDS.map((f) => f.token));

/** True when this token is one Roof Board knows how to fill. */
export function isKnownEmailToken(token: string): boolean {
  return KNOWN_TOKENS.has(token);
}

/**
 * ROOF.link's merge fields, mapped onto ours.
 *
 * `null` means Roof Board has no equivalent and the template needs a person to
 * decide what to do. Those are not silently dropped — the token is left in the
 * body and the template is flagged for review, because a blank where a number
 * used to be is worse than an obvious placeholder.
 */
export const ROOFLINK_FIELDS: Readonly<Record<string, string | null>> = {
  // Customer.
  "job.customer.name": "customer.name",
  "job.customer.first_name": "customer.first_name",
  "job.customer.fn": "customer.first_name",
  "job.customer.last_name": "customer.last_name",
  "job.customer.user.first_name": "customer.first_name",
  "job.customer.user.name": "customer.name",
  "job.customer.user.email": "customer.email",
  "job.customer.email": "customer.email",
  "job.customer.cosigner_first_name": null,
  "job.customer.notes": null,
  "job.customer.phone": "customer.phone",
  "job.customer.cell": "customer.cell",
  "job.customer.address": "customer.address",
  "job.customer.city": "customer.city",
  "job.customer.state": "customer.state",
  "job.customer.zipcode": "customer.zip",
  "payment.estimate.job.customer.user.name": "customer.name",

  // Job.
  "job.id": "job.number",
  "job.job_number": "job.number",
  "job.address": "job.address",
  "job.full_address": "job.full_address",
  "job.city_state_zipcode": "job.city_state_zip",
  "job.full_url": "job.url",
  "job.job_and_bid_type": "job.type",
  "job.job_type_label": "job.type",
  "job.bid_type_label": "job.type",
  "job.lead_status_label": "job.stage",
  "job.customer.source.name": "job.lead_source",
  "job.claim.claim_number": "job.claim_number",
  "job.date_inspection_scheduled": "job.inspection_date",
  "job.date_roof_scheduled": "job.work_date",
  "job.date_trades_scheduled": "job.work_date",
  "job.date_signed": "job.signed_date",
  "matorder.estimate.job.full_address": "job.full_address",
  "workorder.estimate.job.full_address": "job.full_address",
  "job.estimate.job.address": "job.address",
  "job.estimate.job.date_approved": "job.signed_date",
  "job.estimate.job.date_inspection_scheduled": "job.inspection_date",

  // People.
  "job.rep_name": "rep.name",
  "job.customer.rep.name": "rep.name",
  "job.customer.rep.user.name": "rep.name",
  "job.customer.rep.user.first_name": "rep.first_name",
  "job.customer.rep.user.last_name": "rep.last_name",
  "job.customer.rep.email": "rep.email",
  "job.customer.rep.user.email": "rep.email",
  "job.customer.rep.user": "rep.name",
  "job.customer.rep.phone": "rep.phone",
  "job.rep_2_name": "alt_rep.name",
  "job.project_manager_name": "pm.name",
  "job.customer.project_manager.name": "pm.name",
  "job.created_by.name": "created_by.name",
  "job.created_by.user.employee.name": "created_by.name",
  "job.roofing_crew.name": "crew.name",
  "job.inspector.name": "inspector.name",
  "job.inspector.user.first_name": "inspector.name",
  "job.estimate.job.inspector.name": "inspector.name",

  // Money.
  "job.estimate.tot_ex_price": "estimate.total",
  "job.estimate.tot_price": "estimate.total",
  "job.estimate.gt_price": "estimate.total",
  "job.estimate.tot_gross": "estimate.gross_profit",
  "job.estimate.tot_margin": "estimate.margin",
  "job.estimate.tot_mat": "estimate.materials",
  "job.estimate.tot_lab": "estimate.labor",
  "job.balance": "estimate.balance",
  "job.estimate.owes": "estimate.balance",

  // Orders.
  "matorder.id": "material_order.number",
  "workorder.id": "work_order.number",
  "workorder.start_date": "work_order.start_date",
  "workorder.end_date": "work_order.end_date",

  // Us.
  "job.customer.company.name": "company.name",
  "job.customer.company.url": "company.website",
  "job.customer.company.facebook": "company.facebook",
  "job.customer.company.primary_contact_phone": "company.phone",
  "sender.employee.company.name": "company.name",
  "matorder.estimate.job.customer.company.name": "company.name",
  "workorder.estimate.job.customer.company.name": "company.name",
  "sender.name": "sender.name",
  "sender.email": "sender.email",
  "sender.business_or_any_phone": "sender.phone",

  // No equivalent in Roof Board. Each is left in place and flagged.
  "job.date_completed": null,
  "job.estimate.agp_margin": null,
  "job.estimate.change_order_desc": null,
  "job.estimate.change_order_amt": null,
  "job.total_requested_commissions": null,
  "job.marketing_rep": null,
  "job.claim.insurance_company.name": null,
  "job.claim.policy_holder_name": null,
  "job.claim.claim_handler.user.name": null,
  "job.claim.type_of_loss": null,
  "job.claim_supervisor.name": null,
  "job.date_adj_mtg": null,
  "job.estimate.job.date_adj_mtg": null,
  "job.date_first_check": null,
  "job.date_permitted": null,
  "job.job_notes": null,
  "job.customer.date_created": null,
  "job.customer.region.full_address": null,
  "job.customer.project_manager.phone": null,
  "job.checklist_data.final_retail_transition_email.date_completed": null,
  "job.checklist_data.need_to_register_gaf_warranty.date_completed": null,
};

/**
 * Templates the owner asked to leave behind, by their name in the export.
 *
 * Four hand a customer to an outside contractor by name, and the rest are
 * instructions to staff about ROOF.link itself — "set yourself a todo in
 * Rooflink" — which describe a system we are replacing rather than a thing the
 * business does. The owner asked for these to be dropped on 2 Aug 2026; the
 * list is here rather than in the script so the test can prove the count.
 */
export const ROOFLINK_TEMPLATES_TO_DROP: readonly string[] = [
  "Refer Adrian Espinoza (Everything but doors, windows, or interior /shingles only)",
  "Refer David Beckham (Not Roofing)",
  "Refer Drew Spivey (Not Gutters or Roofing)",
  "Refer Fortress Roofing",
  "5 Days Since Someone's Marked Accounting Review",
  "Automated Email for Project Manager",
  "Contract Signed automation",
  "Payment Received",
  "Quality Assurance Complete Email",
  "Quality Assurance Email",
  "Register Rewards",
  "Register Warranty",
  "Schedule Work Order",
];

const DROP_SET = new Set(ROOFLINK_TEMPLATES_TO_DROP);

const ROOFLINK_CONTEXTS: Readonly<Record<string, EmailContext>> = {
  job: "job",
  matorder: "material_order",
  workorder: "work_order",
  payment: "payment",
  company: "company",
};

/**
 * The old company's name, numbers and addresses, replaced by merge fields.
 *
 * Order matters: the email addresses go first, or the bare-domain rule eats
 * their tail and leaves "Insurance@{{company.website}}" behind.
 */
const BRANDING: readonly {
  pattern: RegExp;
  replacement: string;
  note: string;
}[] = [
  {
    pattern: /[\w.+-]+@the\s?roofing\s?dudes\.com/gi,
    replacement: "{{company.email}}",
    note: "an old @theroofingdudes.com address",
  },
  {
    pattern: /(?:https?:\/\/)?(?:www\.)?the\s?roofing\s?dudes\.com/gi,
    replacement: "{{company.website}}",
    note: "the old website",
  },
  {
    pattern: /\(?205\)?[-.\s]?732[-.\s]?7231/g,
    replacement: "{{company.phone}}",
    note: "the old office number",
  },
  {
    pattern: /\bThe\s+Roofing\s+Dudes(?:,?\s+LLC\.?)?/gi,
    replacement: "{{company.name}}",
    note: "the old company name",
  },
  {
    pattern: /\bRoofing\s+Dudes(?:,?\s+LLC\.?)?/gi,
    replacement: "{{company.name}}",
    note: "the old company name",
  },
];

/** Any other phone number, which may be a person's and must not be guessed at. */
const OTHER_PHONE = /\(?\d{3}\)?[-.\s]\d{3}[-.\s]\d{4}/;

/**
 * Take the old company out of a template.
 *
 * Everything the business will rename is replaced with a merge field rather
 * than with the new name, because the new name is not decided — and once it is,
 * it is typed once in Tools instead of ninety times here.
 */
export function scrubBranding(text: string): {
  text: string;
  /** What was swapped for a merge field. Confident — recorded, not asked about. */
  replaced: string[];
  /** What a person has to look at. */
  uncertain: string[];
} {
  let out = text;
  const replaced: string[] = [];
  const uncertain: string[] = [];

  for (const rule of BRANDING) {
    if (rule.pattern.test(out)) {
      // Fresh regex: `test` on a /g pattern leaves lastIndex behind it.
      out = out.replace(
        new RegExp(rule.pattern.source, rule.pattern.flags),
        rule.replacement,
      );
      if (!replaced.includes(rule.note)) replaced.push(rule.note);
    }
    rule.pattern.lastIndex = 0;
  }

  if (OTHER_PHONE.test(out)) {
    uncertain.push("a phone number that is not the office — check whose it is");
  }
  if (/roof\s?\.?\s?link|rooflink/i.test(out)) {
    uncertain.push("a mention of ROOF.link");
  }

  return { text: out, replaced, uncertain };
}

/** Rewrite ROOF.link's merge fields as ours. */
export function translateMergeFields(text: string): {
  text: string;
  notes: string[];
} {
  const unmapped = new Set<string>();
  const unknown = new Set<string>();

  const out = text.replace(/{{\s*([\w.]+)\s*}}/g, (whole, field: string) => {
    if (isKnownEmailToken(field)) return `{{${field}}}`;
    // hasOwn, not a bare index: `{{constructor}}` or `{{toString}}` would
    // otherwise find Object.prototype and splice a function's source into the
    // template instead of being reported as unrecognised.
    const mapped = Object.hasOwn(ROOFLINK_FIELDS, field)
      ? ROOFLINK_FIELDS[field]
      : undefined;
    if (mapped === undefined) {
      unknown.add(field);
      return whole;
    }
    if (mapped === null) {
      unmapped.add(field);
      return whole;
    }
    return `{{${mapped}}}`;
  });

  const notes: string[] = [];
  for (const field of [...unmapped].sort()) {
    notes.push(`{{${field}}} has nothing to fill it in Roof Board`);
  }
  for (const field of [...unknown].sort()) {
    notes.push(`{{${field}}} was not recognised`);
  }
  return { text: out, notes };
}

/**
 * Put the paragraph breaks back where the export can be sure of them.
 *
 * ROOF.link's CSV export flattens the HTML body: 88 of the 98 templates arrive
 * as one unbroken paragraph, with sentences running into the next heading. A
 * full reconstruction would be guesswork, so this only breaks where the old
 * markup left a fingerprint — a numbered list marker, a "#1)" heading, a "P.S."
 * — and leaves the rest for a person to tidy in the editor.
 */
export function reflowExportedBody(text: string): string {
  return text
    .replace(/\r\n?/g, "\n")
    .replace(/(\S)(#\d+\))/g, "$1\n\n$2")
    .replace(/(\S)(\d+\.\t)/g, "$1\n$2")
    .replace(/(\S)(P\.S\.)/g, "$1\n\n$2")
    .replace(/(\S)(Step \d+[:.])/g, "$1\n\n$2")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** What one converted template should be stored as. */
export interface ConvertedTemplate {
  name: string;
  context: EmailContext;
  subject: string;
  body: string;
  needsReview: boolean;
  reviewNotes: string[];
}

/** One row of the ROOF.link export. */
export interface RooflinkTemplateRow {
  name: string;
  context: string;
  subject: string;
  message: string;
}

/** True when this template is one the owner asked to leave out. */
export function isDroppedTemplate(name: string): boolean {
  return DROP_SET.has(name.trim());
}

/**
 * Convert one exported template into what Roof Board stores.
 *
 * Branding comes out, merge fields are rewritten, and anything either step was
 * unsure about is recorded on the template itself so the Emails screen can show
 * the owner a list to work through rather than making him read all ninety.
 */
export function convertRooflinkTemplate(
  row: RooflinkTemplateRow,
): ConvertedTemplate {
  const context = ROOFLINK_CONTEXTS[row.context.trim()] ?? "job";

  const subjectScrub = scrubBranding(row.subject ?? "");
  const subjectFields = translateMergeFields(subjectScrub.text);

  const bodyScrub = scrubBranding(reflowExportedBody(row.message ?? ""));
  const bodyFields = translateMergeFields(bodyScrub.text);

  /**
   * Swapping the old company name for {{company.name}} is not a question — it
   * is recorded so the change is visible, but it does not put the template in
   * the review queue. Flagging all 85 for that would make the queue the same
   * length as the list, and nobody reads a queue that long.
   *
   * What does need a person: a merge field with no Roof Board equivalent, a
   * field nothing recognised, a phone number that might belong to somebody, and
   * any surviving mention of ROOF.link.
   */
  const asked = dedupe([
    ...subjectScrub.uncertain,
    ...bodyScrub.uncertain,
    ...subjectFields.notes,
    ...bodyFields.notes,
  ]);
  const recorded = dedupe([...subjectScrub.replaced, ...bodyScrub.replaced]);

  return {
    name: row.name.trim(),
    context,
    subject: subjectFields.text.trim(),
    body: bodyFields.text,
    needsReview: asked.length > 0,
    reviewNotes: [...asked, ...recorded],
  };
}

function dedupe(values: readonly string[]): string[] {
  const out: string[] = [];
  for (const value of values) if (!out.includes(value)) out.push(value);
  return out;
}

/**
 * Fill a template in.
 *
 * A token with no value is left visible rather than blanked, so a half-filled
 * email cannot be sent without somebody noticing. `unresolvedEmailTokens` is
 * what the send button checks.
 */
export function renderEmailTemplate(
  text: string,
  values: Readonly<Record<string, string | null | undefined>>,
): string {
  return text.replace(/{{\s*([\w.]+)\s*}}/g, (whole, token: string) => {
    const value = lookup(values, token);
    return value == null || value === "" ? whole : value;
  });
}

/**
 * A merge value, or nothing.
 *
 * `values[token]` alone reaches Object.prototype, so a body containing
 * `{{constructor}}` or `{{toString}}` would render a function's source into an
 * email to a customer rather than being left visible as an unfilled token.
 */
function lookup(
  values: Readonly<Record<string, string | null | undefined>>,
  token: string,
): string | null | undefined {
  return Object.hasOwn(values, token) ? values[token] : undefined;
}

/** Every token in this text that `values` cannot fill. */
export function unresolvedEmailTokens(
  text: string,
  values: Readonly<Record<string, string | null | undefined>>,
): string[] {
  const missing = new Set<string>();
  for (const [, token] of text.matchAll(/{{\s*([\w.]+)\s*}}/g)) {
    const value = lookup(values, token!);
    if (value == null || value === "") missing.add(token!);
  }
  return [...missing].sort();
}
